import React, { useState } from 'react';
import { GetEventDetailsQuery, TeamTypesEnum } from 'types/generated/client';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import CloseIcon from 'svg/CloseIcon';
import Modal from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';

type GroupData = NonNullable<GetEventDetailsQuery['eventsByPk']>['groups'][0];
type TeamData = GroupData['teams'][0];
type TeamMemberData = TeamData['members'][0];

interface Props {
  isOpen: boolean;
  closeModal: (value: boolean) => void;
  shareUrl?: string;
  title: string;
  buttonText?: string;
  event: { groups: GroupData[] };
}

// Singles  Component
const Singles = ({ team }: { team: TeamData }) => (
  <div className="overflow-hidden">
    {team.members.map((member: TeamMemberData, i: number) => (
      <div key={i} className="mt-4 flex flex-col items-center justify-center gap-4">
        <img
          src={getProfileImageUrlOrPlaceholder({
            path: member.userProfile?.profileImagePath,
          })}
          alt={`Player ${member.userProfile?.preferredName}`}
          className="object-fit h-12 w-12 rounded-full object-center"
        />
        <p className="typography-product-caption mb-0 pl-2 text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {member.userProfile?.fullName}
        </p>
      </div>
    ))}
  </div>
);

// Common Team Member Component
const TeamMember = ({ member }: { member: TeamMemberData }) => (
  <div className="flex flex-row items-center justify-center">
    <img
      className="h-10 w-10 rounded-full ring ring-color-bg-lightmode-primary dark:ring-color-bg-darkmode-primary"
      src={getProfileImageUrlOrPlaceholder({
        path: member.userProfile?.profileImagePath,
      })}
      alt={`Player ${member.userProfile?.preferredName}`}
    />
    <p className="typography-product-caption mb-0 pl-2 text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
      {member.userProfile?.fullName}
    </p>
  </div>
);

// Component for Unregistered Team Member
const TeamMemberUnregistered = () => (
  <div className="flex flex-row items-center justify-center">
    <img
      className="h-10 w-10 rounded-full ring ring-color-bg-lightmode-primary dark:ring-color-bg-darkmode-primary"
      src={getProfileImageUrlOrPlaceholder({ path: '' })}
      alt={`Player Un Registered`}
    />
    <p className="typography-product-caption mb-0 pl-2 text-center text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
      Partner registration pending
    </p>
  </div>
);

const Doubles = ({ team }: { team: TeamData }) => (
  <div className="pb-10">
    {team.members.map((member: TeamMemberData, i: number) => (
      <div key={i} className={`-mb-11 mt-8 flex`}>
        <TeamMember member={member} />
      </div>
    ))}
    {team.members.length === 1 && (
      <div className={`-mb-11 mt-8 flex`}>
        <TeamMemberUnregistered />
      </div>
    )}
  </div>
);

const TabContent = ({ activeTeams }: { activeTeams: GroupData }) => {
  return (
    <>
      <div
        className={classNames(
          `grid gap-4`,
          activeTeams?.teamType === TeamTypesEnum.Doubles
            ? 'md:grid-cols-3'
            : 'xs:grid-cols-3 md:grid-cols-8',
        )}
      >
        {activeTeams?.teams.map((team: TeamData, index: number) => (
          <div key={index}>
            {activeTeams.teamType === TeamTypesEnum.Doubles ? (
              <Doubles team={team} />
            ) : (
              <Singles team={team} />
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default function ModalPlayers({ isOpen, closeModal, shareUrl, title, event }: Props) {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Modal
      isOpen={isOpen}
      className="block rounded-lg focus:outline-none focus:ring-4"
      classNamePosition="relative"
      classNameMaxWidth="md:max-w-[69.5rem] xs:max-w-xs"
      handleClose={() => closeModal(false)}
    >
      <div className="">
        <div className="flex justify-between border border-b border-color-border-input-lightmode px-10 py-6 text-center dark:border-color-border-input-darkmode">
          <h3 className="typography-product-heading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {title}
          </h3>
          <div className="w-25">
            <button
              className="p-0.5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
              type="button"
              onClick={() => closeModal(false)}
            >
              <CloseIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
        {/* Render tabs */}
        <div className="mb-4 overflow-y-scroll px-10 pt-6">
          <div className="flex w-full flex-row flex-nowrap gap-2 overflow-x-auto overflow-y-visible pb-6">
            {event?.groups?.map((group: GroupData, index: number) => {
              return (
                <button
                  key={index}
                  className={classNames(
                    `flex shrink-0 flex-col items-center justify-center rounded-md px-8 py-3`,
                    activeTab === index
                      ? '  bg-color-bg-darkmode-primary'
                      : ' bg-color-bg-lightmode-secondary',
                  )}
                  onClick={() => setActiveTab(index)}
                >
                  <div
                    className={classNames(
                      'typography-product-chips-filters',
                      activeTab === index
                        ? 'text-color-text-darkmode-primary'
                        : 'text-color-text-lightmode-secondary',
                    )}
                  >
                    {group.title}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Render content of active tab */}
          <div className="mt-2 pb-6">
            <h2
              className={classNames(
                'typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary ',
                event?.groups[activeTab]?.teamType === TeamTypesEnum.Doubles ? '' : 'pb-6',
              )}
            >
              {(event?.groups[activeTab]?.teams?.length ?? 0) <=
                (event?.groups[activeTab]?.teamLimit ?? 0) && (
                <>
                  <span className="pr-2">
                    {event?.groups[activeTab]?.teamType === TeamTypesEnum.Doubles
                      ? 'Teams registered'
                      : 'Players registered'}
                  </span>
                  <span>
                    {event?.groups[activeTab]?.teams?.length}/{event?.groups[activeTab]?.teamLimit}
                  </span>
                </>
              )}
            </h2>
            <div className="h-80 min-h-10">
              <TabContent activeTeams={event?.groups[activeTab]} />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
